/**
 * Useful, common hooks
 */
import React, { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router';

import {
  deleteEmoticon,
  getEmoticonsByUsername,
  loadUsersEmoticons,
  updateEmoticon,
  uploadEmoticon,
} from '~/modules/emoticons';
import { isLoading, setLoading } from '~/modules/loading';
import {
  TYPE_CUSTOM,
  fetchPlayingStream,
  setAddingStream,
  setViewType,
} from '~/modules/streams';

import { useRouter } from '~/routerContext';

/**
 * Returns a useCallback-wrapped function
 * that adds a stream to the current view
 */
export const useAddStream = () => {
  const dispatch = useDispatch();
  const {
    streams: { byUsername },
    sections: { playing },
  } = useSelector((state) => state.streams);

  /**
   * @type {(stream: Stream, e?: Event) => void}
   */
  const addStreamCallback = useCallback(
    (stream, e) => {
      const username = stream.username || stream.slug;

      if (e) {
        e.preventDefault();
        e.stopPropagation();
      }

      if (playing.indexOf(username) !== -1) {
        return;
      }

      dispatch(fetchPlayingStream(username, true));
      dispatch(setViewType(TYPE_CUSTOM));
      dispatch(setAddingStream(false));
    },
    [dispatch, playing]
  );

  return addStreamCallback;
};

/**
 * Loads the emoticons for a user, provides CRUD functions
 * @param {String} username
 */
export const useEmoticons = (username, loadEmoticons = true) => {
  const dispatch = useDispatch();

  /**
   * @type {Emoticon[]}
   */
  const emoticons = useSelector((state) =>
    getEmoticonsByUsername(state, username)
  );

  const loading = useSelector((state) => isLoading(state.loading, 'EMOTICONS'));

  useEffect(() => {
    if (emoticons.length === 0 && loadEmoticons) {
      dispatch(setLoading('EMOTICONS', true));
      dispatch(loadUsersEmoticons()).then(() => {
        dispatch(setLoading('EMOTICONS', false));
      });
    }
  }, [dispatch]);

  /**
   * @param {Number} id
   */
  const remove = useCallback(
    (id) => {
      return dispatch(deleteEmoticon(id));
    },
    [dispatch]
  );

  const update = useCallback(
    (emoticon) => dispatch(updateEmoticon(emoticon)),
    [dispatch]
  );

  const upload = useCallback(
    (emoticon) => dispatch(uploadEmoticon(emoticon)),
    [dispatch]
  );

  return {
    loading,
    emoticons,
    remove,
    upload,
    update,
  };
};

/** @deprecated Use React-Router hooks directly, no more HoCs in modern React code. */
export function withRouter(Component) {
  function ComponentWithRouterProp(props) {
    const location = useLocation();
    const navigate = useNavigate();
    const router = useRouter();
    const params = useParams();
    const additionalProps = {
      location,
      navigate,
      params,
      router,
    };
    return <Component {...props} {...additionalProps} />;
  }

  return ComponentWithRouterProp;
}
